import React, { FC, useCallback, useEffect, useState } from 'react';
import * as S from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { authInfoSelect } from '@entities/auth';
import { AppDispatch } from '@config/store';
import { getCardForms } from '@api/thunks/card';
import { cardSelect } from '@entities/card';
import { Table } from 'antd';
import { getCardsColumns } from './ui/cardsColumns/cardsColumns';
import { ICardForm } from '@entities/card/types';
import { CreateCardFormModal } from './ui/cardFormModal';
import { Icons } from '@assets';
import { CreateFormModal } from './ui/createFormModal';
import { UpdateCardFormModal } from './ui/updateFormModal';
import { optionsRegFormSelect } from '@utils/const';
import { ERegFormAction } from '@utils';
import {
    deleteFormRequest,
    disableFormRequest,
    enableFormRequest,
} from '@api/services/cardApi';
import { checkIsResponseStatus } from '@utils/functions';
import { AxiosResponse } from 'axios';
import { IOkResponse } from '@types';

const CardsPage: FC = () => {
    const { shop } = useSelector(authInfoSelect);
    const dispatch = useDispatch<AppDispatch>();
    const { forms, loading } = useSelector(cardSelect);
    const [selectedForm, setSelectedForm] = useState<ICardForm | null>(null);
    const [openedPopup, setOpenedPopup] = useState<boolean>(false);
    const [openedCreateModal, setOpenedCreateModal] = useState<boolean>(false);
    const [openedUpdateModal, setOpenedUpdateModal] = useState<boolean>(false);
    const [form, setForm] = useState<number>(0);
    const [selectAction, setSelectAction] = useState<ERegFormAction>(
        ERegFormAction.ACTIVE
    );

    const [selectedRowKey, setSelectedRowKey] = useState<React.Key | null>(
        null
    );

    const handleAddNewForm = () => {
        setOpenedCreateModal(true);
    };

    const handlerClickId = useCallback(
        (id: number, e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
            e.preventDefault();
            const form = forms.find(form => form.id === id);
            if (form) {
                setSelectedForm(form);
            }
            setOpenedPopup(true);
        },
        [forms]
    );
    const onChangeSelect = (value: number) => {
        setForm(value);
        setSelectAction(optionsRegFormSelect[value - 1].label);
    };

    const handleAction = async () => {
        if (!shop || !selectedRowKey) return;

        const handleSuccess = (res: AxiosResponse<IOkResponse>) => {
            if (checkIsResponseStatus(res.status)) {
                dispatch(getCardForms(shop.id));
            }
        };

        switch (form) {
            case 1:
                const disableRes = await disableFormRequest(
                    shop.id,
                    selectedRowKey
                );
                handleSuccess(disableRes);
                break;

            case 2:
                const enableRes = await enableFormRequest(
                    shop.id,
                    selectedRowKey
                );
                handleSuccess(enableRes);
                break;

            case 3:
                setOpenedUpdateModal(true);
                break;

            case 4:
                const deleteRes = await deleteFormRequest(
                    shop.id,
                    selectedRowKey
                );
                handleSuccess(deleteRes);
                break;

            default:
                console.warn('Unknown form action:', form);
        }
    };

    const renderTableFooter = () => {
        return (
            <S.FooterWrap>
                <div>
                    <S.ActionWrap>
                        <S.StyledSelect
                            options={optionsRegFormSelect}
                            value={selectAction}
                            onChange={val => onChangeSelect(val as number)}
                        />
                        <S.StyledActionButton onClick={handleAction}>
                            <span>Применить</span>
                        </S.StyledActionButton>
                    </S.ActionWrap>
                </div>
            </S.FooterWrap>
        );
    };

    const handleRowDoubleClick = (record: ICardForm) => {
        if (record.id === selectedRowKey) {
            setSelectedRowKey(null);
        }
    };

    const rowSelection = {
        type: 'radio' as const,
        selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
        onChange: (selectedRowKeys: React.Key[]) => {
            const newSelectedKey = selectedRowKeys[0];

            if (newSelectedKey === selectedRowKey) {
                setSelectedRowKey(null);
            } else {
                setSelectedRowKey(newSelectedKey || null);
            }
        },
    };

    const rowProps = (record: ICardForm) => {
        return {
            onDoubleClick: () => handleRowDoubleClick(record),
        };
    };

    useEffect(() => {
        if (shop) {
            dispatch(getCardForms(shop.id));
        }
    }, [shop, dispatch]);

    const columns = getCardsColumns(handlerClickId);

    return (
        <S.Wrapper>
            <>
                <S.HeaderWrapper>
                    <S.Title>Формы регистрации</S.Title>
                    <S.StyledAddButton onClick={handleAddNewForm}>
                        <img src={Icons.AddWhitePlusInCircle} alt="icon plus" />
                        <span>Создать форму</span>
                    </S.StyledAddButton>
                </S.HeaderWrapper>

                <S.TableWrapper>
                    <Table
                        size="small"
                        columns={columns}
                        onRow={rowProps}
                        rowSelection={rowSelection}
                        dataSource={forms}
                        rowKey={rec => rec.id}
                        scroll={{ x: 700 }}
                        loading={loading === 'loading'}
                        footer={renderTableFooter}
                        pagination={{
                            pageSize: 50,
                            showSizeChanger: true,
                            pageSizeOptions: ['50', '100'],
                        }}
                    />
                </S.TableWrapper>
                <S.Overlay
                    display={
                        openedPopup || openedCreateModal || openedUpdateModal
                            ? 'block'
                            : 'none'
                    }
                />
                {openedPopup && selectedForm && (
                    <CreateCardFormModal
                        form={selectedForm}
                        modalVisibilityToggler={() => setOpenedPopup(false)}
                    />
                )}
                {openedCreateModal && (
                    <CreateFormModal
                        modalVisibilityToggler={() =>
                            setOpenedCreateModal(false)
                        }
                    />
                )}
                {openedUpdateModal && selectedRowKey && (
                    <UpdateCardFormModal
                        modalVisibilityToggler={() =>
                            setOpenedUpdateModal(false)
                        }
                        cardFormId={selectedRowKey}
                    />
                )}
            </>
        </S.Wrapper>
    );
};

export default CardsPage;
